import { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Menu, Typography, usePopover, useScreenSize } from '@robocorp/components';
import {
  IconCode,
  IconDownloadCloud,
  IconHelp,
  IconLightBulb,
  IconPageFlip,
  IconPuzzle,
} from '@robocorp/icons/iconic';
import { DocsLinks, ExternalLinks } from '~/constants';
import { useSelector } from '~/hooks';

const HelpMenuBase = forwardRef<HTMLDivElement, Record<string, unknown>>((props, forwardedRef) => {
  const { t } = useTranslation();
  const showButtonLabel = !useScreenSize('m');

  return (
    <Box ref={forwardedRef} {...props}>
      <Menu
        trigger={
          <Button icon={IconHelp} variant="ghost">
            {showButtonLabel ? t('help') : undefined}
          </Button>
        }
        placement="bottom"
      >
        <Menu.Link icon={IconCode} href={DocsLinks.LINK_PYTHON_QUICKSTART} target="_blank">
          {t('helpMenu.quickStart')}
        </Menu.Link>

        <Menu.Link icon={IconPageFlip} href={DocsLinks.PRODUCT_MANUAL} target="_blank">
          {t('helpMenu.documentation')}
        </Menu.Link>

        <Menu.Link icon={IconPuzzle} href={ExternalLinks.ROBOCORP_PORTAL} target="_blank">
          {t('helpMenu.portal')}
        </Menu.Link>

        <Menu.Link
          icon={IconLightBulb}
          href={DocsLinks.CHANGELOG}
          target="_blank"
          className="rn-badge"
        >
          {t('helpMenu.releaseNotes')}
        </Menu.Link>

        <Menu.Link icon={IconDownloadCloud} href={ExternalLinks.DOWNLOADS} target="_blank">
          {t('helpMenu.downloads')}
        </Menu.Link>
      </Menu>
    </Box>
  );
});

export const HelpMenu = () => {
  const { t } = useTranslation();

  const [helpTooltipOpen, setHelpTooltipOpen] = useState(false);
  const { referenceRef, referenceProps, PopoverContent } = usePopover({
    open: helpTooltipOpen,
    placement: 'top',
  });

  const dismissingGettingStarted = useSelector(
    (state) => state.entityStates.users.current.dismissingGettingStarted,
  );

  useEffect(() => {
    if (dismissingGettingStarted) {
      setHelpTooltipOpen(true);
    }
  }, [dismissingGettingStarted]);

  return (
    <>
      <PopoverContent>
        <Box
          display="flex"
          flexDirection="column"
          flex={1}
          p="$16"
          borderRadius="$8"
          boxShadow="medium"
          backgroundColor="background.primary"
        >
          <Typography fontSize="$12" mb={8}>
            {t('onboarding.helpMenuInfo')}
          </Typography>
          <span>
            <Button size="small" variant="secondary" onClick={() => setHelpTooltipOpen(false)}>
              {t('hide')}
            </Button>
          </span>
        </Box>
      </PopoverContent>
      <HelpMenuBase ref={referenceRef} {...referenceProps} />
    </>
  );
};
